<script setup lang="ts">
import QRCode from 'qrcode'

const props = defineProps<{
  visible: boolean
  codeUrl: string | null
  price: string
  orderId?: string | null
}>()

const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void
  (e: 'close'): void
}>()

const canvasRef = ref<HTMLCanvasElement | null>(null)

// Generate QR Code when url changes or visible becomes true
watch(() => [props.codeUrl, props.visible, canvasRef.value], async () => {
  if (props.visible && props.codeUrl && canvasRef.value) {
    try {
      await QRCode.toCanvas(canvasRef.value, props.codeUrl, {
        width: 200,
        margin: 1,
        color: {
          dark: '#000000',
          light: '#ffffff'
        }
      })
    } catch (err) {
      console.error('QR Code generation failed:', err)
    }
  }
}, { immediate: true })

const handleClose = () => {
  emit('update:visible', false)
  emit('close')
}

const { t } = useI18n()
</script>

<template>
  <el-dialog
    :model-value="visible"
    @update:model-value="handleClose"
    width="400px"
    center
    destroy-on-close
    :close-on-click-modal="false"
    class="wx-pay-dialog custom-dialog"
    :show-close="true"
  >
    <template #header>
      <div class="text-center pt-2">
        <h3 class="text-lg font-bold text-gray-900">{{ t('pay.payment.scanTitle') }}</h3>
      </div>
    </template>

    <div class="flex flex-col items-center justify-center w-full pb-4">
      <!-- Amount Display -->
      <div class="mb-6 text-center w-full">
        <div class="text-sm text-gray-500 mb-1">{{ t('pay.payment.amount') }}</div>
        <div class="text-3xl font-bold text-primary-600 font-mono">{{ price }}</div>
      </div>

      <!-- QR Code Container -->
      <div class="relative mb-6 group flex justify-center w-full">
        <div class="p-3 border border-gray-200 rounded-xl bg-white shadow-sm transition-shadow duration-300 hover:shadow-md">
          <canvas ref="canvasRef" class="block w-[200px] h-[200px]"></canvas>
          
          <!-- Wechat Logo Overlay (Optional visual enhancement) -->
          <div class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-lg p-1 shadow-sm flex items-center justify-center pointer-events-none">
            <Icon name="ri:wechat-pay-fill" class="w-8 h-8 text-[#09BB07]" />
          </div>
        </div>
      </div>
      
      <!-- Scan Tip -->
      <div class="flex items-center justify-center text-sm text-white bg-[#09BB07] py-2.5 px-6 rounded-full shadow-sm mb-6 w-fit mx-auto">
        <Icon name="ri:scan-line" class="w-5 h-5 mr-2" />
        <span class="font-medium">{{ t('pay.payment.scanTip') }}</span>
      </div>

      <!-- Order Info -->
      <div class="w-full bg-gray-50 rounded-lg p-3 border border-gray-100">
        <div v-if="orderId" class="flex justify-center gap-2 items-center text-xs text-gray-500 mb-1">
          <span>{{ t('pay.payment.orderNo') || '订单编号' }}:</span>
          <span class="font-mono text-gray-700 select-all">{{ orderId }}</span>
        </div>
        <div class="flex justify-center gap-2 items-center text-xs text-gray-500">
          <span>{{ t('pay.payment.payMethod') || '支付方式' }}:</span>
          <span class="flex items-center text-gray-700">
            <Icon name="ri:wechat-pay-fill" class="w-3.5 h-3.5 text-[#09BB07] mr-1" />
            {{ t('pay.payment.methods.wechat') }}
          </span>
        </div>
      </div>
    </div>
  </el-dialog>
</template>

<style scoped>
.custom-dialog :deep(.el-dialog__header) {
  margin-right: 0;
  padding-bottom: 10px;
  border-bottom: 1px solid #f3f4f6;
}

.custom-dialog :deep(.el-dialog__body) {
  padding-top: 20px;
  padding-bottom: 20px;
}

/* Ensure font-mono works if not globally available */
.font-mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}
</style>
